/**
 * Bloques de contenido de un modulo.
 *
 * Un modulo es una lista ordenada de bloques con tipo. Cada bloque
 * es una unidad que el alumno recorre en orden: un video, un texto,
 * un aviso destacado o un recurso descargable.
 *
 * Sustituye a la plantilla fija de `module_content` (un video arriba
 * y un texto debajo), que impedia alternar varios videos con texto.
 */

import { getSupabase, isSupabaseEnabled } from '@/lib/supabase'

export type BlockKind = 'video' | 'text' | 'callout' | 'resource' | 'quiz' | 'calculator' | 'image'

/**
 * Los tipos de bloque que el alumno puede completar, y por tanto los
 * unicos que cuentan para el avance.
 *
 * Un aviso y un recurso descargable se leen o se bajan, pero nada
 * avisa a la plataforma de que ha pasado, asi que contarlos ponia un
 * techo al progreso que nadie podia superar.
 */
export const PASOS_COMPLETABLES: BlockKind[] = ['text', 'video', 'quiz', 'calculator']
export type VideoSize = 'full' | 'large' | 'medium' | 'compact'

export interface ModuleBlock {
  id: string
  courseSlug: string
  moduleSlug: string
  lang: string
  kind: BlockKind
  title: string | null
  bodyMd: string | null
  videoUrl: string | null
  videoSize: VideoSize
  /** Ancho en pixeles. 0 = ancho completo. */
  videoWidth: number
  durationSec: number
  /** Capitulos del video, uno por linea: "4:30 Titulo". */
  chapters: string | null
  sortOrder: number
}

function map(r: Record<string, unknown>): ModuleBlock {
  return {
    id: r.id as string,
    courseSlug: r.course_slug as string,
    moduleSlug: r.module_slug as string,
    lang: (r.lang as string) ?? 'es',
    kind: (r.kind as BlockKind) ?? 'text',
    title: (r.title as string) ?? null,
    bodyMd: (r.body_md as string) ?? null,
    videoUrl: (r.video_url as string) ?? null,
    videoSize: (r.video_size as VideoSize) ?? 'large',
    videoWidth: (r.video_width as number) ?? 800,
    durationSec: (r.duration_sec as number) ?? 0,
    chapters: (r.chapters as string) ?? null,
    sortOrder: (r.sort_order as number) ?? 0,
  }
}

// ── Lectura ───────────────────────────────────────────────

export async function listBlocks(
  courseSlug: string,
  moduleSlug: string,
  lang = 'es',
): Promise<ModuleBlock[]> {
  if (!isSupabaseEnabled()) return []
  const supabase = getSupabase()
  if (!supabase) return []

  const { data, error } = await supabase
    .from('module_block')
    .select('*')
    .eq('course_slug', courseSlug)
    .eq('module_slug', moduleSlug)
    .eq('lang', lang)
    .order('sort_order')

  if (error) {
    console.info('[blockDb] listBlocks:', error.message)
    return []
  }
  return (data ?? []).map(map)
}

/** Cuenta bloques por modulo, para marcar los vacios en el admin. */
export async function countBlocksByModule(
  courseSlug: string,
  lang = 'es',
): Promise<Map<string, number>> {
  const out = new Map<string, number>()
  if (!isSupabaseEnabled()) return out
  const supabase = getSupabase()
  if (!supabase) return out

  const { data, error } = await supabase
    .from('module_block')
    .select('module_slug')
    .eq('course_slug', courseSlug)
    .eq('lang', lang)

  if (error || !data) return out
  for (const r of data) {
    const slug = r.module_slug as string
    out.set(slug, (out.get(slug) ?? 0) + 1)
  }
  return out
}

// ── Escritura ─────────────────────────────────────────────

export async function createBlock(input: {
  courseSlug: string
  moduleSlug: string
  lang?: string
  kind: BlockKind
  title?: string | null
  bodyMd?: string | null
  videoUrl?: string | null
  videoSize?: VideoSize
  videoWidth?: number
  /** Posicion donde insertarlo. Por defecto, al final. */
  atIndex?: number
}): Promise<ModuleBlock> {
  if (!isSupabaseEnabled()) throw new Error('Sin conexión con la base de datos')
  const supabase = getSupabase()
  if (!supabase) throw new Error('Sin conexión con la base de datos')

  const lang = input.lang ?? 'es'
  const existing = await listBlocks(input.courseSlug, input.moduleSlug, lang)
  const index = input.atIndex ?? existing.length

  // Hacer hueco si se inserta en medio
  if (index < existing.length) {
    for (let i = index; i < existing.length; i++) {
      await supabase.from('module_block')
        .update({ sort_order: i + 1 })
        .eq('id', existing[i].id)
    }
  }

  const base = {
    course_slug: input.courseSlug,
    module_slug: input.moduleSlug,
    lang,
    kind: input.kind,
    title: input.title ?? null,
    body_md: input.bodyMd ?? null,
    video_url: input.videoUrl ?? null,
    video_size: input.videoSize ?? 'large',
    sort_order: index,
  }

  let { data, error } = await supabase
    .from('module_block')
    .insert({ ...base, video_width: input.videoWidth ?? 800 })
    .select()
    .maybeSingle()

  // Si la columna de ancho todavia no existe en la BD, reintentar sin ella
  if (error && /video_width/.test(error.message)) {
    ;({ data, error } = await supabase
      .from('module_block')
      .insert(base)
      .select()
      .maybeSingle())
  }

  if (error || !data) {
    const msg = error?.message ?? 'La base de datos no devolvio el bloque'
    console.error('[blockDb] createBlock:', msg)
    throw new Error(msg)
  }
  return map(data)
}

export async function updateBlock(id: string, patch: Partial<{
  title: string | null
  bodyMd: string | null
  videoUrl: string | null
  videoSize: VideoSize
  /** Ancho en pixeles. 0 = ancho completo. */
  videoWidth: number
  durationSec: number
  /** Capitulos del video, uno por linea. */
  chapters: string | null
  sortOrder: number
}>): Promise<void> {
  if (!isSupabaseEnabled()) throw new Error('Sin conexión con la base de datos')
  const supabase = getSupabase()
  if (!supabase) throw new Error('Sin conexión con la base de datos')

  const dbPatch: Record<string, unknown> = {}
  if (patch.title !== undefined) dbPatch.title = patch.title
  if (patch.bodyMd !== undefined) dbPatch.body_md = patch.bodyMd
  if (patch.chapters !== undefined) dbPatch.chapters = patch.chapters
  if (patch.videoUrl !== undefined) dbPatch.video_url = patch.videoUrl
  if (patch.videoSize !== undefined) dbPatch.video_size = patch.videoSize
  if (patch.videoWidth !== undefined) dbPatch.video_width = patch.videoWidth
  if (patch.durationSec !== undefined) dbPatch.duration_sec = patch.durationSec
  if (patch.sortOrder !== undefined) dbPatch.sort_order = patch.sortOrder

  let { error } = await supabase.from('module_block').update(dbPatch).eq('id', id)

  if (error && /video_width/.test(error.message)) {
    const { video_width: _drop, ...rest } = dbPatch as Record<string, unknown>
    ;({ error } = await supabase.from('module_block').update(rest).eq('id', id))
  }

  if (error) throw new Error(error.message)
}

export async function deleteBlock(id: string): Promise<void> {
  if (!isSupabaseEnabled()) throw new Error('Sin conexión con la base de datos')
  const supabase = getSupabase()
  if (!supabase) throw new Error('Sin conexión con la base de datos')

  const { error } = await supabase.from('module_block').delete().eq('id', id)
  if (error) throw new Error(error.message)
}

/** Reordena: recibe la lista completa de ids en su nuevo orden. */
export async function reorderBlocks(orderedIds: string[]): Promise<void> {
  if (!isSupabaseEnabled()) throw new Error('Sin conexión con la base de datos')
  const supabase = getSupabase()
  if (!supabase) throw new Error('Sin conexión con la base de datos')

  for (let i = 0; i < orderedIds.length; i++) {
    const { error } = await supabase
      .from('module_block')
      .update({ sort_order: i })
      .eq('id', orderedIds[i])
    if (error) throw new Error(error.message)
  }
}

/** Duplica un bloque justo debajo del original. */
export async function duplicateBlock(block: ModuleBlock): Promise<ModuleBlock> {
  return createBlock({
    courseSlug: block.courseSlug,
    moduleSlug: block.moduleSlug,
    lang: block.lang,
    kind: block.kind,
    title: block.title,
    bodyMd: block.bodyMd,
    videoUrl: block.videoUrl,
    videoSize: block.videoSize,
    videoWidth: block.videoWidth,
    atIndex: block.sortOrder + 1,
  })
}

// ── Progreso por bloque ───────────────────────────────────

export interface BlockProgressRow {
  blockId: string
  positionSec: number
  completed: boolean
}

export async function fetchBlockProgress(blockIds: string[]): Promise<Map<string, BlockProgressRow>> {
  const out = new Map<string, BlockProgressRow>()
  if (!isSupabaseEnabled() || blockIds.length === 0) return out
  const supabase = getSupabase()
  if (!supabase) return out

  // Filtrado por usuario a mano, no confiando en RLS.
  //
  // `block_progress` tiene dos politicas de lectura y se combinan con
  // OR: la propia (auth.uid() = user_id) y una de administrador que
  // deja ver las filas de todo el mundo. Para un alumno normal
  // bastaba con la primera, pero a quien administra la plataforma le
  // devolvia el avance de todos mezclado con el suyo, asi que veia
  // modulos empezados que no habia tocado.
  const { data: sesion } = await supabase.auth.getUser()
  const uid = sesion?.user?.id
  if (!uid) return out

  const { data, error } = await supabase
    .from('block_progress')
    .select('*')
    .eq('user_id', uid)
    .in('block_id', blockIds)

  if (error || !data) return out
  for (const r of data) {
    out.set(r.block_id as string, {
      blockId: r.block_id as string,
      positionSec: (r.position_sec as number) ?? 0,
      completed: Boolean(r.completed),
    })
  }
  return out
}

export async function saveBlockProgress(
  blockId: string,
  position?: number,
  completed?: boolean,
): Promise<void> {
  if (!isSupabaseEnabled()) return
  const supabase = getSupabase()
  if (!supabase) return

  const { error } = await supabase.rpc('save_block_progress', {
    p_block_id: blockId,
    p_position: position ?? null,
    p_completed: completed ?? null,
  })

  // Se propaga en vez de tragarselo: perder el avance sin avisar es
  // de las cosas que hacen que alguien no vuelva.
  if (error) {
    console.warn('[blockDb] no se pudo guardar el avance:', error.message)
    throw new Error(error.message)
  }
}

/**
 * Copia todos los bloques de un modulo a otro, en ambos idiomas.
 * Se usa al duplicar un modulo para no rehacer la estructura.
 */
export async function copyBlocksToModule(
  courseSlug: string,
  fromModuleSlug: string,
  toModuleSlug: string,
): Promise<number> {
  if (!isSupabaseEnabled()) throw new Error('Sin conexión con la base de datos')
  const supabase = getSupabase()
  if (!supabase) throw new Error('Sin conexión con la base de datos')

  const { data, error } = await supabase
    .from('module_block')
    .select('*')
    .eq('course_slug', courseSlug)
    .eq('module_slug', fromModuleSlug)
    .order('sort_order')

  if (error) throw new Error(error.message)
  if (!data || data.length === 0) return 0

  const rows = data.map(r => ({
    course_slug: courseSlug,
    module_slug: toModuleSlug,
    lang: r.lang,
    kind: r.kind,
    title: r.title,
    body_md: r.body_md,
    video_url: r.video_url,
    video_size: r.video_size,
    video_width: r.video_width,
    sort_order: r.sort_order,
  }))

  const { error: insErr } = await supabase.from('module_block').insert(rows)
  if (insErr) throw new Error(insErr.message)
  return rows.length
}

/**
 * Copia la estructura de bloques de un idioma a otro, conservando
 * los videos y los titulos, para no rehacerla al traducir.
 * El texto se copia tal cual como punto de partida.
 *
 * No sobreescribe: si el idioma destino ya tiene bloques, no hace nada.
 */
export async function copyBlocksToLang(
  courseSlug: string,
  moduleSlug: string,
  fromLang: string,
  toLang: string,
): Promise<number> {
  if (!isSupabaseEnabled()) throw new Error('Sin conexión con la base de datos')
  const supabase = getSupabase()
  if (!supabase) throw new Error('Sin conexión con la base de datos')

  const target = await listBlocks(courseSlug, moduleSlug, toLang)
  if (target.length > 0) return 0

  const source = await listBlocks(courseSlug, moduleSlug, fromLang)
  if (source.length === 0) return 0

  const rows = source.map(b => ({
    course_slug: courseSlug,
    module_slug: moduleSlug,
    lang: toLang,
    kind: b.kind,
    title: b.title,
    body_md: b.bodyMd,
    video_url: b.videoUrl,
    video_size: b.videoSize,
    video_width: b.videoWidth,
    sort_order: b.sortOrder,
  }))

  const { error } = await supabase.from('module_block').insert(rows)
  if (error) throw new Error(error.message)
  return rows.length
}

/**
 * Estado de todos los modulos de un curso: cuantos bloques tiene
 * cada uno y cuantos ha completado el alumno.
 *
 * Dos consultas para el curso entero, en vez de una por modulo.
 */
export interface ModuleCompletion {
  total: number
  done: number
  completed: boolean
}

export async function fetchCourseCompletion(
  courseSlug: string,
  lang = 'es',
): Promise<Map<string, ModuleCompletion>> {
  const out = new Map<string, ModuleCompletion>()
  if (!isSupabaseEnabled()) return out
  const supabase = getSupabase()
  if (!supabase) return out

  /*
    Solo cuentan los bloques que el alumno puede completar.

    El avance contaba todos los bloques del modulo, incluidos los que
    no tienen forma de marcarse: un aviso y un recurso descargable se
    leen o se bajan, pero nada avisa a la plataforma de que ha
    pasado. Como `completed` exige que esten todos, **ningun modulo
    del curso podia terminarse**: los seis tienen al menos un aviso.

    Eso dejaba el "0 de 6 lecciones" clavado en cero para siempre, el
    porcentaje con un techo por debajo del 100% y el certificado
    inalcanzable.

    Se cuentan los tipos que el reproductor sabe marcar: texto (al
    leerlo), video (al verlo), ejercicio (al responderlo) y
    calculadora (al abrirla). El aviso, el recurso y la imagen siguen
    en la leccion, simplemente no puntuan.
  */
  const { data: blocks, error } = await supabase
    .from('module_block')
    .select('id, module_slug, kind')
    .eq('course_slug', courseSlug)
    .eq('lang', lang)
    .in('kind', PASOS_COMPLETABLES)

  if (error || !blocks || blocks.length === 0) return out

  // Cuantos bloques tiene cada modulo
  const byModule = new Map<string, string[]>()
  for (const b of blocks) {
    const slug = b.module_slug as string
    const list = byModule.get(slug) ?? []
    list.push(b.id as string)
    byModule.set(slug, list)
  }

  // Cuales ha completado
  // Mismo motivo que en fetchBlockProgress: sin este filtro, quien
  // administra la plataforma ve el avance de todos los alumnos como
  // si fuera suyo.
  const { data: sesion } = await supabase.auth.getUser()
  const uid = sesion?.user?.id
  if (!uid) return out

  const { data: prog } = await supabase
    .from('block_progress')
    .select('block_id, completed')
    .eq('user_id', uid)
    .in('block_id', blocks.map(b => b.id as string))

  const doneIds = new Set(
    (prog ?? []).filter(p => p.completed).map(p => p.block_id as string),
  )

  for (const [slug, ids] of byModule) {
    const done = ids.filter(id => doneIds.has(id)).length
    out.set(slug, { total: ids.length, done, completed: ids.length > 0 && done === ids.length })
  }

  return out
}

// ── Preguntas de un bloque de quiz ────────────────────────

export interface BlockQuestionOption {
  id: string
  text: string
  correct?: boolean
}

export interface BlockQuestion {
  id: string
  blockId: string
  prompt: string
  options: BlockQuestionOption[]
  explanation: string | null
  /** Imagen que acompana al enunciado, si la hay. */
  imageUrl: string | null
  /** Que se ve en ella. Sin esto, quien no la ve no puede responder. */
  imageAlt: string | null
  sortOrder: number
}

function mapQuestion(r: Record<string, unknown>): BlockQuestion {
  return {
    id: r.id as string,
    blockId: r.block_id as string,
    prompt: (r.prompt as string) ?? '',
    options: (r.options as BlockQuestionOption[]) ?? [],
    explanation: (r.explanation as string) ?? null,
    imageUrl: (r.image_url as string) ?? null,
    imageAlt: (r.image_alt as string) ?? null,
    sortOrder: (r.sort_order as number) ?? 0,
  }
}

export async function listQuestions(blockId: string): Promise<BlockQuestion[]> {
  if (!isSupabaseEnabled()) return []
  const supabase = getSupabase()
  if (!supabase) return []

  const { data, error } = await supabase
    .from('block_question')
    .select('*')
    .eq('block_id', blockId)
    .order('sort_order')

  if (error) {
    console.info('[blockDb] listQuestions:', error.message)
    return []
  }
  return (data ?? []).map(mapQuestion)
}

export async function createQuestion(input: {
  blockId: string
  prompt?: string
  options?: BlockQuestionOption[]
  explanation?: string | null
  imageUrl?: string | null
  imageAlt?: string | null
}): Promise<BlockQuestion> {
  if (!isSupabaseEnabled()) throw new Error('Sin conexión con la base de datos')
  const supabase = getSupabase()
  if (!supabase) throw new Error('Sin conexión con la base de datos')

  const existing = await listQuestions(input.blockId)

  const { data, error } = await supabase
    .from('block_question')
    .insert({
      block_id: input.blockId,
      prompt: input.prompt ?? '',
      // Cuatro opciones vacias: es lo habitual y evita empezar de cero
      options: input.options ?? [
        { id: 'A', text: '', correct: true },
        { id: 'B', text: '' },
        { id: 'C', text: '' },
        { id: 'D', text: '' },
      ],
      explanation: input.explanation ?? null,
      image_url: input.imageUrl ?? null,
      image_alt: input.imageAlt ?? null,
      sort_order: existing.length,
    })
    .select()
    .maybeSingle()

  if (error) throw new Error(error.message)
  if (!data) throw new Error('El servidor no devolvió la pregunta creada')
  return mapQuestion(data)
}

export async function updateQuestion(id: string, patch: Partial<{
  prompt: string
  options: BlockQuestionOption[]
  explanation: string | null
  imageUrl: string | null
  imageAlt: string | null
  sortOrder: number
}>): Promise<void> {
  if (!isSupabaseEnabled()) throw new Error('Sin conexión con la base de datos')
  const supabase = getSupabase()
  if (!supabase) throw new Error('Sin conexión con la base de datos')

  const dbPatch: Record<string, unknown> = {}
  if (patch.prompt !== undefined) dbPatch.prompt = patch.prompt
  if (patch.options !== undefined) dbPatch.options = patch.options
  if (patch.explanation !== undefined) dbPatch.explanation = patch.explanation
  if (patch.imageUrl !== undefined) dbPatch.image_url = patch.imageUrl
  if (patch.imageAlt !== undefined) dbPatch.image_alt = patch.imageAlt
  if (patch.sortOrder !== undefined) dbPatch.sort_order = patch.sortOrder

  const { error } = await supabase.from('block_question').update(dbPatch).eq('id', id)
  if (error) throw new Error(error.message)
}

export async function deleteQuestion(id: string): Promise<void> {
  if (!isSupabaseEnabled()) throw new Error('Sin conexión con la base de datos')
  const supabase = getSupabase()
  if (!supabase) throw new Error('Sin conexión con la base de datos')

  const { error } = await supabase.from('block_question').delete().eq('id', id)
  if (error) throw new Error(error.message)
}

/**
 * Guarda la respuesta a una pregunta.
 *
 * Sirve para saber que preguntas se fallan mas y poder revisarlas.
 * Si falla el guardado no se interrumpe al alumno: es telemetria,
 * no parte del ejercicio.
 */
export async function saveQuestionAttempt(
  questionId: string,
  chosen: string,
  correct: boolean,
): Promise<void> {
  if (!isSupabaseEnabled()) return
  const supabase = getSupabase()
  if (!supabase) return

  const { data: userData } = await supabase.auth.getUser()
  const userId = userData?.user?.id
  if (!userId) return

  const { error } = await supabase.from('question_attempt').insert({
    user_id: userId,
    question_id: questionId,
    chosen,
    correct,
  })

  if (error) console.info('[blockDb] saveQuestionAttempt:', error.message)
}

/**
 * Todas las preguntas de un curso, para el repaso.
 *
 * El repaso necesita el banco entero, no el de una leccion. Dos
 * consultas en vez de una por modulo.
 */
export interface CourseQuestion extends BlockQuestion {
  moduleSlug: string
}

export async function fetchCourseQuestions(
  courseSlug: string,
  lang = 'es',
): Promise<CourseQuestion[]> {
  if (!isSupabaseEnabled()) return []
  const supabase = getSupabase()
  if (!supabase) return []

  const { data: blocks, error } = await supabase
    .from('module_block')
    .select('id, module_slug')
    .eq('course_slug', courseSlug)
    .eq('lang', lang)
    .eq('kind', 'quiz')

  if (error || !blocks || blocks.length === 0) return []

  const byBlock = new Map(blocks.map(b => [b.id as string, b.module_slug as string]))

  const { data: questions, error: qErr } = await supabase
    .from('block_question')
    .select('*')
    .in('block_id', [...byBlock.keys()])
    .order('sort_order')

  if (qErr || !questions) return []

  return questions.map(r => ({
    ...mapQuestion(r),
    moduleSlug: byBlock.get(r.block_id as string) ?? '',
  }))
}

/**
 * Bloques sin contenido, por modulo.
 *
 * Un modulo con tres bloques vacios pasa la comprobacion de
 * publicacion, porque solo se miraba cuantos hay. Esto cuenta los
 * que no tienen nada que ensenar: un texto en blanco, un video sin
 * enlace, una calculadora sin elegir.
 */
/**
 * Bloques de texto repetidos por modulo.
 *
 * Al probar el editor es facil dejar un bloque duplicado: se copia
 * uno para no empezar de cero y no se borra el original. Al alumno le
 * llega el mismo parrafo dos o tres veces seguidas. El modulo 1 del
 * curso tenia el mismo texto tres veces (D-420), y no se veia desde
 * el panel porque nada lo miraba.
 *
 * Cuenta cuantos textos sobran: si el mismo contenido aparece tres
 * veces, sobran dos. Compara el texto normalizado, ignorando los
 * `###` de markdown y los espacios, para que "Tu punto de partida" y
 * "### Tu punto de partida" cuenten como el mismo.
 */
export async function countDuplicateTextBlocksByModule(
  courseSlug: string,
  lang = 'es',
): Promise<Map<string, number>> {
  const out = new Map<string, number>()
  if (!isSupabaseEnabled()) return out
  const supabase = getSupabase()
  if (!supabase) return out

  const { data, error } = await supabase
    .from('module_block')
    .select('module_slug, body_md')
    .eq('course_slug', courseSlug)
    .eq('lang', lang)
    .eq('kind', 'text')

  if (error || !data) return out

  const normaliza = (t: string) =>
    t.replace(/#+/g, '').replace(/\s+/g, ' ').trim().toLowerCase()

  const porModulo = new Map<string, Map<string, number>>()
  for (const r of data as { module_slug: string; body_md: string | null }[]) {
    const texto = normaliza(r.body_md ?? '')
    if (texto === '') continue // los vacios ya los cuenta la otra funcion
    const vistos = porModulo.get(r.module_slug) ?? new Map<string, number>()
    vistos.set(texto, (vistos.get(texto) ?? 0) + 1)
    porModulo.set(r.module_slug, vistos)
  }

  for (const [modulo, vistos] of porModulo) {
    let sobran = 0
    for (const n of vistos.values()) if (n > 1) sobran += n - 1
    if (sobran > 0) out.set(modulo, sobran)
  }

  return out
}

export async function countEmptyBlocksByModule(
  courseSlug: string,
  lang = 'es',
): Promise<Map<string, number>> {
  const out = new Map<string, number>()
  if (!isSupabaseEnabled()) return out
  const supabase = getSupabase()
  if (!supabase) return out

  const { data, error } = await supabase
    .from('module_block')
    .select('id, module_slug, kind, body_md, video_url, title')
    .eq('course_slug', courseSlug)
    .eq('lang', lang)

  if (error || !data) return out

  // Los ejercicios se llenan con preguntas, en otra tabla
  const idsQuiz = (data as Record<string, unknown>[])
    .filter(r => r.kind === 'quiz')
    .map(r => r.id as string)

  const conPreguntas = new Set<string>()
  if (idsQuiz.length > 0) {
    const { data: preguntas } = await supabase
      .from('block_question')
      .select('block_id')
      .in('block_id', idsQuiz)

    for (const p of preguntas ?? []) {
      conPreguntas.add((p as { block_id: string }).block_id)
    }
  }

  for (const r of data as Record<string, unknown>[]) {
    const kind = r.kind as string
    const texto = ((r.body_md as string) ?? '').trim()
    const url = ((r.video_url as string) ?? '').trim()

    // Que hace falta para que ese bloque ensene algo
    const vacio =
      (kind === 'text' && texto === '') ||
      (kind === 'callout' && texto === '') ||
      (kind === 'video' && url === '') ||
      (kind === 'resource' && url === '') ||
      (kind === 'calculator' && url === '') ||
      (kind === 'quiz' && !conPreguntas.has(r.id as string))

    if (vacio) {
      const m = r.module_slug as string
      out.set(m, (out.get(m) ?? 0) + 1)
    }
  }

  return out
}
